import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "../../api/client";
import LineItemTable from "../shared/LineItemTable";
import StatusBadge from "../shared/StatusBadge";
import "./QuoteBuilder.css";

const formatCAD = (n) =>
    new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
    }).format(n ?? 0);

const BLANK_ITEM = { service_id: "", description: "", qty: 1, unit_price: 0 };

const defaultExpiry = () => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().split("T")[0];
};

// API may return a plain date or a full ISO timestamp — normalize to just
// the date part so it works as an <input type="date"> value.
const toDateOnly = (value) => (value ? String(value).split("T")[0] : "");

const QuoteBuilder = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const isEdit = Boolean(id);

    const [clients, setClients] = useState([]);
    const [services, setServices] = useState([]);
    const [projects, setProjects] = useState([]);
    const [form, setForm] = useState({
        client_id: searchParams.get("client") || "",
        project_id: searchParams.get("project") || "",
        title: "",
        notes: "",
        expires_at: defaultExpiry(),
        status: "draft",
    });
    const [items, setItems] = useState([{ ...BLANK_ITEM }]);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [converting, setConverting] = useState(false);
    const [dueDate, setDueDate] = useState(defaultExpiry());
    const [showConvert, setShowConvert] = useState(false);
    const [sentAt, setSentAt] = useState(null);
    const [sending, setSending] = useState(false);
    const [sendSuccess, setSendSuccess] = useState("");

    useEffect(() => {
        Promise.all([api.allClients(), api.services()]).then(([cRes, sRes]) => {
            setClients(cRes.data || []);
            setServices(sRes.services || []);
        });

        if (isEdit) {
            api.quote(id)
                .then(({ quote }) => {
                    setForm({
                        client_id: quote.client_id,
                        project_id: quote.project_id || "",
                        title: quote.title,
                        notes: quote.notes || "",
                        expires_at: toDateOnly(quote.expires_at) || defaultExpiry(),
                        status: quote.status,
                    });
                    setSentAt(quote.sent_at || null);
                    setItems(
                        quote.line_items?.map((li) => ({
                            id: li.id,
                            service_id: li.service_id || "",
                            description: li.description,
                            qty: parseFloat(li.qty),
                            unit_price: parseFloat(li.unit_price),
                        })) || [{ ...BLANK_ITEM }],
                    );
                })
                .catch(() => setError(t("common.error")))
                .finally(() => setLoading(false));
        }
    }, [id, isEdit, t]);

    // Load the selected client's projects so the quote can be attached
    // to one (scope additions / change requests).
    useEffect(() => {
        if (!form.client_id) {
            setProjects([]);
            return;
        }
        api.clientProjects(form.client_id)
            .then((res) => setProjects(res.projects || []))
            .catch(() => setProjects([]));
    }, [form.client_id]);

    const subtotal = items.reduce((s, i) => s + i.qty * i.unit_price, 0);

    const updateItem = (index, changes) => {
        setItems((prev) =>
            prev.map((item, i) =>
                i === index ? { ...item, ...changes } : item,
            ),
        );
    };
    const addItem = () => setItems((prev) => [...prev, { ...BLANK_ITEM }]);
    const removeItem = (index) =>
        setItems((prev) => prev.filter((_, i) => i !== index));

    const handleFormChange = (e) =>
        setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            const payload = {
                ...form,
                project_id: form.project_id || null,
                tax_rate: 0,
                line_items: items.map((i) => ({
                    service_id: i.service_id || null,
                    description: i.description,
                    qty: i.qty,
                    unit_price: i.unit_price,
                })),
            };
            if (isEdit) {
                await api.updateQuote(id, payload);
            } else {
                await api.createQuote(payload);
            }
            navigate("/admin/quotes");
        } catch (err) {
            setError(err.message || t("common.error"));
        } finally {
            setSaving(false);
        }
    };

    const handleSendEmail = async () => {
        if (
            sentAt &&
            !window.confirm(
                `This quote was already emailed on ${new Date(sentAt).toLocaleDateString("en-CA")}. Send it again?`,
            )
        )
            return;
        setSending(true);
        setError("");
        setSendSuccess("");
        try {
            const res = await api.sendQuoteEmail(id);
            setForm((p) => ({ ...p, status: res.quote.status }));
            setSentAt(res.quote.sent_at);
            setSendSuccess(res.message || "Quote sent.");
        } catch (err) {
            setError(err.message || t("common.error"));
        } finally {
            setSending(false);
        }
    };

    const handleConvert = async () => {
        if (!dueDate) return;
        setConverting(true);
        try {
            const { invoice } = await api.convertToInvoice(id, {
                due_date: dueDate,
            });
            navigate(`/admin/invoices/${invoice.id}`);
        } catch (err) {
            setError(err.message);
        } finally {
            setConverting(false);
        }
    };

    if (loading)
        return (
            <p style={{ color: "var(--color-text-muted)" }}>
                {t("common.loading")}
            </p>
        );

    return (
        <div className="quote-builder">
            <div className="quote-builder__header">
                <div className="section-header">
                    <h2>{isEdit ? t("quote.edit") : t("quote.new")}</h2>
                </div>
                {isEdit && (
                    <div className="quote-builder__header-actions">
                        <StatusBadge status={form.status} />
                        {["draft", "sent"].includes(form.status) && (
                            <button
                                type="button"
                                className="btn btn--ghost btn--sm"
                                onClick={handleSendEmail}
                                disabled={sending}
                                title={
                                    sentAt
                                        ? `Last sent ${new Date(sentAt).toLocaleDateString("en-CA")}`
                                        : "Email this quote to the client"
                                }
                            >
                                {sending
                                    ? "Sending…"
                                    : sentAt
                                      ? t("quote.resendEmail")
                                      : t("quote.sendEmail")}
                            </button>
                        )}
                        {["draft", "sent"].includes(form.status) && (
                            <button
                                className="btn btn--primary btn--sm"
                                onClick={async () => {
                                    if (
                                        !window.confirm(
                                            "Approve this quote on the client's behalf? " +
                                                (form.project_id
                                                    ? "It will be attached to the linked project."
                                                    : "A project will be created."),
                                        )
                                    )
                                        return;
                                    try {
                                        const res = await api.acceptQuote(id);
                                        setForm((p) => ({
                                            ...p,
                                            status: "accepted",
                                        }));
                                        if (res.project) {
                                            navigate(
                                                `/admin/projects/${res.project.id}`,
                                            );
                                        }
                                    } catch (err) {
                                        setError(err.message);
                                    }
                                }}
                                title="Approve this quote on the client's behalf"
                            >
                                Approve Quote
                            </button>
                        )}
                        {form.status === "sent" && (
                            <button
                                className="btn btn--ghost btn--sm"
                                onClick={async () => {
                                    if (
                                        !window.confirm(
                                            "Mark this quote as declined?",
                                        )
                                    )
                                        return;
                                    try {
                                        await api.declineQuote(id);
                                        setForm((p) => ({
                                            ...p,
                                            status: "declined",
                                        }));
                                    } catch (err) {
                                        setError(err.message);
                                    }
                                }}
                            >
                                Mark Declined
                            </button>
                        )}
                        {form.project_id ? (
                            <button
                                className="btn btn--ghost btn--sm"
                                onClick={() =>
                                    navigate(
                                        `/admin/projects/${form.project_id}`,
                                    )
                                }
                            >
                                View Project
                            </button>
                        ) : (
                            <button
                                className="btn btn--ghost btn--sm"
                                onClick={async () => {
                                    try {
                                        const { project } =
                                            await api.convertToProject(id);
                                        navigate(
                                            `/admin/projects/${project.id}`,
                                        );
                                    } catch (err) {
                                        setError(err.message);
                                    }
                                }}
                                title="Create a project from this quote"
                            >
                                Create Project
                            </button>
                        )}
                        {!showConvert && (
                            <button
                                className="btn btn--ghost btn--sm"
                                onClick={() => setShowConvert(true)}
                            >
                                {t("quote.convertToInvoice")}
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => navigate(`/dashboard/quotes/${id}`)}
                            title="Preview the client-facing quote and print / save as PDF"
                        >
                            View / Print
                        </button>
                    </div>
                )}
            </div>

            {error && <div className="error-message">{error}</div>}
            {sendSuccess && (
                <div className="success-message">{sendSuccess}</div>
            )}

            {showConvert && (
                <div className="quote-builder__convert-panel">
                    <p>{t("quote.convertToInvoice")} — set due date:</p>
                    <input
                        type="date"
                        className="quote-builder__date-input"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                    />
                    <button
                        className="btn btn--primary btn--sm"
                        onClick={handleConvert}
                        disabled={!dueDate || converting}
                    >
                        {converting ? "Converting…" : "Confirm"}
                    </button>
                    <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => setShowConvert(false)}
                    >
                        {t("common.cancel")}
                    </button>
                </div>
            )}

            <form onSubmit={handleSave}>
                <div className="quote-builder__meta card">
                    <div className="quote-builder__meta-grid">
                        <div className="form-group">
                            <label>{t("quote.client")}</label>
                            <select
                                name="client_id"
                                value={form.client_id}
                                onChange={handleFormChange}
                                required
                            >
                                <option value="">— Select client —</option>
                                {clients
                                    .filter((c) => c.role === "client")
                                    .map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name} ({c.email})
                                        </option>
                                    ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>{t("quote.quoteTitle")}</label>
                            <input
                                name="title"
                                value={form.title}
                                onChange={handleFormChange}
                                required
                                placeholder="e.g. Website Redesign 2026"
                            />
                        </div>
                        <div className="form-group">
                            <label>Attach to Project (optional)</label>
                            <select
                                name="project_id"
                                value={form.project_id}
                                onChange={handleFormChange}
                                disabled={!form.client_id}
                            >
                                <option value="">
                                    — None (new project on acceptance) —
                                </option>
                                {projects.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>{t("quote.expires")}</label>
                            <input
                                type="date"
                                name="expires_at"
                                value={form.expires_at}
                                onChange={handleFormChange}
                            />
                        </div>
                        {isEdit && (
                            <div className="form-group">
                                <label>{t("quote.status")}</label>
                                <select
                                    name="status"
                                    value={form.status}
                                    onChange={handleFormChange}
                                >
                                    {[
                                        "draft",
                                        "sent",
                                        "accepted",
                                        "declined",
                                    ].map((s) => (
                                        <option key={s} value={s}>
                                            {t(`status.${s}`)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                    <div className="form-group">
                        <label>{t("quote.notes")}</label>
                        <textarea
                            name="notes"
                            value={form.notes}
                            onChange={handleFormChange}
                            placeholder="Optional notes visible to client…"
                        />
                    </div>
                </div>

                <div className="quote-builder__items card">
                    <div className="section-header">
                        <h3>Line Items</h3>
                    </div>
                    <LineItemTable
                        items={items}
                        editable
                        services={services}
                        onItemChange={updateItem}
                        onAddItem={addItem}
                        onRemoveItem={removeItem}
                    />
                    <div className="quote-builder__totals">
                        <div className="quote-builder__total-row quote-builder__total-row--grand">
                            <span>{t("totals.total")}</span>
                            <span>{formatCAD(subtotal)}</span>
                        </div>
                    </div>
                </div>

                <div className="quote-builder__footer">
                    <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => navigate("/admin/quotes")}
                    >
                        {t("common.cancel")}
                    </button>
                    <button
                        type="submit"
                        className="btn btn--primary"
                        disabled={saving}
                    >
                        {saving ? t("common.loading") : t("common.save")}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default QuoteBuilder;
